import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Link2Off, FileCheck2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { TAGLINE } from "@/lib/constants";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import { PropertyCard } from "@/components/site/PropertyCard";
import { DealCard } from "@/components/site/DealCard";

const HERO = "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const PILLARS = [
  { icon: Link2Off, label: "Chain-free only" },
  { icon: FileCheck2, label: "Proof of funds checked" },
  { icon: MapPin, label: "UK, London & Greater London" },
  { icon: ShieldCheck, label: "Your details stay private" },
];

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-6">
        <p className="eyebrow" data-testid="hero-tagline">{TAGLINE}</p>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]" data-testid="hero-heading">
          Buy or sell in the UK <em className="text-[#9F7E44]">without the chain.</em>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#57534E] leading-relaxed max-w-xl">
          We connect proceedable buyers with sellers who want to move quickly. Buyers prove their funds up front, sellers skip the waiting, and we manage every step in between, privately.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/buy" data-testid="hero-cta-buyer"
            className="group h-14 px-8 inline-flex items-center justify-center gap-3 bg-[#121417] text-white text-sm font-medium hover:bg-[#9F7E44] transition-colors duration-300">
            I'm a ready buyer <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/sell" data-testid="hero-cta-seller"
            className="h-14 px-8 inline-flex items-center justify-center border border-[#121417] text-sm font-medium hover:bg-[#F3F1ED] transition-colors duration-300">
            I want to sell fast
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-6 relative">
        <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden grain">
          <img src={HERO} alt="London skyline over the River Thames" className="h-full w-full object-cover" />
        </div>
        <div className="absolute -bottom-6 left-4 sm:-left-8 bg-white/90 backdrop-blur-xl border border-[#E4E1D9] p-5 max-w-[260px] shadow-[0_20px_60px_-20px_rgba(18,20,23,.25)]">
          <p className="eyebrow">Every buyer</p>
          <p className="mt-2 font-display text-lg leading-snug">AIP or proof of funds reviewed before any introduction.</p>
        </div>
      </Reveal>
    </div>
    <div className="border-y border-[#E4E1D9] bg-[#F3F1ED]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {PILLARS.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 text-sm text-[#121417]">
            <Icon className="h-5 w-5 text-[#9F7E44] shrink-0" strokeWidth={1.5} /> {label}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Comparison = () => (
  <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" id="why">
    <SectionHead eyebrow="Why chain-free" title="Chains are where sales stall. We remove them.">
      In a typical sale, your move depends on everyone else's. One delay below you and the whole line waits. We only work with buyers who can proceed now.
    </SectionHead>
    <div className="mt-14 grid md:grid-cols-12 gap-6">
      <Reveal className="md:col-span-5 bg-white border border-[#E4E1D9] p-8">
        <p className="font-plex text-xs uppercase tracking-widest text-[#847F77]">The usual route</p>
        <ul className="mt-6 space-y-4 text-[#57534E]">
          {["Buyers who still need to sell first", "Long waits for the chain to line up", "Sales falling through late on", "Your details passed around many parties"].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 bg-[#C5BCAB] shrink-0" />{t}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1} className="md:col-span-7 bg-[#121417] text-white p-8 relative overflow-hidden">
        <p className="font-plex text-xs uppercase tracking-widest text-[#C5A46A]">Our route</p>
        <ul className="mt-6 space-y-4">
          {["Buyers with an AIP or proof of funds, reviewed by us", "No onward chain: buyer status checked before introduction", "One point of contact managing both sides", "Buyer and seller details never shared directly"].map((t) => (
            <li key={t} className="flex gap-3 text-white/90"><span className="mt-2 h-1.5 w-1.5 bg-[#C5A46A] shrink-0" />{t}</li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

const STEPS = {
  buyers: ["Create your account", "Share your budget, areas & chain status", "Upload your AIP or proof of funds", "We review and match you to suitable properties"],
  sellers: ["Create your account", "Tell us about your property & timeline", "We review and discuss the next steps with you", "We introduce verified, chain-free buyers"],
};

const HowItWorks = () => (
  <section className="bg-[#F3F1ED] border-y border-[#E4E1D9]" id="how-it-works">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <SectionHead eyebrow="How it works" title="Two paths. One trusted middle." />
      <div className="mt-14 grid md:grid-cols-2 gap-10">
        {Object.entries(STEPS).map(([k, steps], col) => (
          <Reveal key={k} delay={col * 0.1}>
            <h3 className="font-display text-2xl">{k === "buyers" ? "For buyers" : "For sellers"}</h3>
            <ol className="mt-6 border-l border-[#C5BCAB]">
              {steps.map((s, i) => (
                <li key={s} className="relative pl-8 pb-7 last:pb-0">
                  <span className="absolute -left-[13px] top-0 grid h-6 w-6 place-items-center bg-[#FBFBF9] border border-[#9F7E44] font-plex text-[11px] text-[#9F7E44]">{i + 1}</span>
                  <p className="text-[#121417]">{s}</p>
                </li>
              ))}
            </ol>
            <Link to={k === "buyers" ? "/buy" : "/sell"} data-testid={`how-cta-${k}`}
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#9F7E44] hover:text-[#866835]">
              {k === "buyers" ? "Register as a buyer" : "Submit your property"} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Showcase = ({ items, kind }) => {
  if (!items.length) return null;
  const isDeals = kind === "deals";
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <SectionHead eyebrow={isDeals ? "Track record" : "Available now"}
          title={isDeals ? "Completed deals" : "A small, curated selection"} />
        <Link to={isDeals ? "/completed" : "/properties"} data-testid={`showcase-view-all-${kind}`}
          className="inline-flex items-center gap-2 text-sm font-medium hover:text-[#9F7E44]">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.slice(0, 3).map((x, i) => (
          <Reveal key={x.id} delay={i * 0.08}>{isDeals ? <DealCard d={x} /> : <PropertyCard p={x} />}</Reveal>
        ))}
      </div>
    </section>
  );
};

const FinalCta = () => (
  <section className="max-w-7xl mx-auto px-6 sm:px-10">
    <div className="relative overflow-hidden bg-[#121417] text-white p-10 sm:p-16 grain grid md:grid-cols-12 gap-8 items-center">
      <div className="md:col-span-8">
        <p className="eyebrow !text-[#C5A46A]">Confidential by design</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl leading-tight">You deal with us. Never a stranger.</h2>
        <p className="mt-4 text-white/75 max-w-xl">Buyers and sellers never get each other's contact details. We handle introductions, viewings and negotiations on your behalf.</p>
      </div>
      <div className="md:col-span-4 flex flex-col gap-3">
        <Link to="/buy" data-testid="final-cta-buyer" className="h-12 inline-flex items-center justify-center bg-[#9F7E44] text-sm hover:bg-[#866835] transition-colors">Register as a buyer</Link>
        <Link to="/sell" data-testid="final-cta-seller" className="h-12 inline-flex items-center justify-center border border-white/40 text-sm hover:bg-white hover:text-[#121417] transition-colors">Sell your property</Link>
      </div>
    </div>
  </section>
);

export default function Landing() {
  const [props, setProps] = useState([]);
  const [deals, setDeals] = useState([]);
  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="landing-page">
      <Hero />
      <Comparison />
      <HowItWorks />
      <Showcase items={props} kind="properties" />
      <div className="bg-[#F3F1ED] border-y border-[#E4E1D9]"><Showcase items={deals} kind="deals" /></div>
      <div className="pt-24"><FinalCta /></div>
    </div>
  );
}
